import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Documents due (ADR tenant-export-deletion-and-consent): when `GET /me` lists a document whose
// current version the person has not accepted, the app is blocked behind this screen until they
// accept each one, then `POST /me/consents` records it. The gate is the whole behaviour; the
// screen is the part a person sees.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AuthShell, Button, Checkbox, FormControlLabel, FormGroup, Link, ListSkeleton, Stack, Typography, } from '@platform/design-system';
import { useSession } from "../auth/session.js";
import { describeError, toAppError } from "../http/errors.js";
import { authShellFrame } from "../screens/sign-in-screen.js";
import { LEGAL_DOCUMENT_LABEL, NOTICE_CHANNEL_LABEL } from "./copy.js";
import { privacyKeys, useAcceptDocuments, useMe } from "./queries.js";
const TICK_ALL = 'Tick each box above to continue.';
const CHANGED = 'These documents were just updated. Read them again, then tick each box to continue.';
/** The blocking screen: each new document with its link and a box, then one button. */
export function DocumentsDueScreen({ frame, documents, askNoticeChannel, busy, error, onAccept, onSignOut, }) {
    const [agreed, setAgreed] = useState({});
    const [notices, setNotices] = useState(false);
    const [tried, setTried] = useState(false);
    const ready = documents.every((d) => agreed[d.document] === true) && (!askNoticeChannel || notices);
    return (_jsx(AuthShell, { ...authShellFrame(frame), title: "We\u2019ve updated our documents", lead: "Please read the new versions and agree to them to keep using your account. Nothing in your account has changed.", children: _jsxs(Stack, { spacing: 2.5, component: "form", noValidate: true, onSubmit: (event) => {
                event.preventDefault();
                setTried(true);
                if (ready)
                    onAccept({ noticeChannel: askNoticeChannel });
            }, children: [error && _jsx(Alert, { severity: "error", children: error }), _jsxs(FormGroup, { "aria-label": "Documents to agree to", children: [documents.map((d) => (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: agreed[d.document] === true, onChange: (event) => setAgreed((current) => ({ ...current, [d.document]: event.target.checked })) }), label: _jsxs(_Fragment, { children: [LEGAL_DOCUMENT_LABEL[d.document].before, _jsx(Link, { href: d.url, target: "_blank", rel: "noopener noreferrer", children: LEGAL_DOCUMENT_LABEL[d.document].link })] }) }, d.document))), askNoticeChannel && (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: notices, onChange: (event) => setNotices(event.target.checked) }), label: NOTICE_CHANNEL_LABEL }))] }), tried && !ready && (_jsx(Typography, { variant: "body2", color: "error", role: "alert", children: TICK_ALL })), _jsx(Button, { type: "submit", variant: "contained", size: "large", busy: busy, busyLabel: "Saving\u2026", children: "Agree and continue" }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["Not now?", ' ', _jsx(Link, { component: "button", type: "button", variant: "body2", onClick: onSignOut, children: "Sign out" })] })] }) }));
}
/**
 * Wraps the signed-in app: while `GET /me` lists documents due, only the acceptance screen shows.
 * While it loads, a skeleton (nothing of the app is shown before we know). If `/me` cannot be
 * read the app shows as usual: the pages behind it fail in plain words on their own.
 */
export function DocumentsDueGate({ frame, onSignedOut, children, }) {
    const me = useMe();
    const accept = useAcceptDocuments();
    const { principal, signOut } = useSession();
    const client = useQueryClient();
    const [error, setError] = useState(null);
    if (me.isLoading)
        return (_jsx(AuthShell, { ...authShellFrame(frame), title: "One moment", children: _jsx(ListSkeleton, { rows: 2 }) }));
    const due = me.data?.documentsDue ?? [];
    if (due.length === 0)
        return _jsx(_Fragment, { children: children });
    const submit = ({ noticeChannel }) => {
        setError(null);
        accept.mutate({
            accepted: due.map((d) => ({ document: d.document, version: d.version })),
            ...(noticeChannel ? { noticeChannel: 'email' } : {}),
        }, {
            onError: (e) => {
                if (toAppError(e).status === 409) {
                    // A newer version landed while the screen was open: show it, ask again.
                    setError(CHANGED);
                    void client.invalidateQueries({
                        queryKey: privacyKeys.me(principal?.tenantId ?? null),
                    });
                }
                else
                    setError(describeError(e, 'We couldn’t save that. Try again.'));
            },
        });
    };
    return (_jsx(DocumentsDueScreen
    // Remount when the versions change, so every box starts unticked for the new ones.
    , { frame: frame, documents: due, askNoticeChannel: me.data?.noticeChannel === undefined, busy: accept.isPending, error: error, onAccept: submit, onSignOut: () => void signOut().then(onSignedOut) }, due.map((d) => `${d.document}@${d.version}`).join(',')));
}
//# sourceMappingURL=documents-due.js.map